import { useTranslation } from 'react-i18next'
import {
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
} from '@/shared/ui/dropdown-menu'
import { useToast } from '@/shared/hooks/use-toast'
import { errorToUserMessage } from '@/api/errors'
import { useMyStudios, useSwitchStudio } from '@/features/auth/hooks/useStudios'
import { Check, Loader2 } from 'lucide-react'

interface StudioSwitcherProps {
  /** Only fetch while the menu holding these items is actually open. */
  enabled: boolean
  /** Closes the surrounding menu once a switch is under way. */
  onSwitching?: () => void
}

/**
 * The in-app studio switcher (runbook part 1 §1): moving between studios you
 * demonstrably belong to, without signing out and back in.
 *
 * Rendered as items inside the existing user menu rather than as new chrome —
 * it appears only for the identities that have somewhere to switch TO, and it
 * stays reachable one-handed on a phone, where that menu already lives in the
 * top bar.
 *
 * Renders nothing at all for the overwhelmingly common single-studio case, so
 * the menu a normal member sees is unchanged.
 */
export function StudioSwitcher({ enabled, onSwitching }: StudioSwitcherProps) {
  const { t } = useTranslation()
  const { toast } = useToast()

  const studios = useMyStudios(enabled)
  const switchStudio = useSwitchStudio()

  const all = studios.data?.studios ?? []
  if (all.length < 2) return null

  const handleSelect = (slug: string) => {
    if (slug === studios.data?.current) return
    onSwitching?.()
    // Where a successful switch LANDS is owned by useSwitchStudio, not by this
    // callback object: `onSwitching` above closes the menu that renders this
    // component, so by the time the mutation settles we are unmounted and
    // TanStack Query drops every mutate-scoped callback. A navigation written
    // here silently never happened. See the hook for the destination and why.
    switchStudio.mutate(slug, {
      // Known consequence of that same unmount: this toast is dropped too, so a
      // failed switch from the user menu currently reports nothing. Left as-is
      // deliberately — where the message should surface once the menu is gone is
      // a UX decision, not a mechanical fix. Tracked alongside F12.
      onError: (err: unknown) =>
        toast({
          title: t('layout:studioSwitcher.failedTitle'),
          description: errorToUserMessage(err, t),
          variant: 'destructive',
        }),
    })
  }

  return (
    <>
      <DropdownMenuLabel className="text-xs font-normal text-muted-foreground">
        {t('layout:studioSwitcher.label')}
      </DropdownMenuLabel>
      {all.map(studio => {
        const isCurrent = studio.slug === studios.data?.current
        return (
          <DropdownMenuItem
            key={studio.slug}
            disabled={isCurrent || switchStudio.isPending}
            onClick={() => handleSelect(studio.slug)}
          >
            {switchStudio.isPending && switchStudio.variables === studio.slug ? (
              <Loader2 size={14} className="mr-2 shrink-0 animate-spin" />
            ) : (
              <Check
                size={14}
                className={`mr-2 shrink-0 ${isCurrent ? '' : 'invisible'}`}
              />
            )}
            <span className="truncate">{studio.name}</span>
          </DropdownMenuItem>
        )
      })}
      <DropdownMenuSeparator />
    </>
  )
}
